import { AnimatedSection } from "@/components/common/AnimatedSection"
import { SectionLabel } from "@/components/common/SectionLabel"
import { Button } from "@/components/ui/Button"
import { BLOG_POSTS } from "@/data"
import { useSEO } from "@/hooks/useSEO"
import { CTASection } from "@/sections/CTASection"
import { createFileRoute, Link, Navigate } from "@tanstack/react-router"
import { motion } from "framer-motion"
import { ArrowLeft, ArrowRight, Calendar, Clock, User } from "lucide-react"

export const Route = createFileRoute("/blog/$slug")({
  component: BlogPostPage,
})

// Inline post body content keyed by slug
const POST_BODIES: Record<string, string[]> = {
  "understanding-payment-orchestration": [
    "Payment orchestration has become one of the most important infrastructure decisions for any business processing payments at scale. Rather than being locked into a single acquirer or processor, modern businesses need the flexibility to route transactions dynamically, fail over automatically, and optimise for approval rates across different geographies.",
    "At its core, payment orchestration sits between your application and your payment processors. It abstracts away the complexity of managing multiple integrations, providing a single API layer that handles routing logic, retry rules, currency conversion, and settlement scheduling.",
    "The business case is compelling. Companies using payment orchestration typically see a 1–3% improvement in approval rates, which translates directly to revenue recovered. For a business processing $10M monthly, that's between $100K and $300K in additional recognised revenue annually.",
    "Orchestration also provides resilience. When a processor experiences downtime — and they do — traffic automatically routes to the next best alternative. Without orchestration, a processor outage means lost revenue. With it, the impact is reduced to milliseconds of failover.",
    "From a compliance perspective, orchestration helps businesses manage geographic complexity. Different countries have different card scheme rules, local payment method requirements, and regulatory constraints. A well-designed orchestration layer encapsulates these rules so your application code doesn't have to.",
    "Looking ahead to 2026, we're seeing orchestration expand beyond card payments into open banking and real-time payment rails. The businesses investing in orchestration infrastructure now will be best positioned to take advantage of these emerging payment channels as they mature.",
  ],
  "pci-dss-v4-what-you-need-to-know": [
    "PCI DSS v4.0 represents the most significant update to the Payment Card Industry Data Security Standard in over a decade. While the core principles remain unchanged — protect cardholder data — the updated standard introduces greater flexibility in how businesses can achieve compliance, alongside new requirements targeting modern threat vectors.",
    'The most significant change in v4.0 is the introduction of the "customised approach." Previously, compliance was binary: you either met the defined requirements or you didn\'t. The customised approach allows organisations with mature security programmes to demonstrate equivalent security through compensating controls, provided they can show the control achieves the stated objective.',
    "New in v4.0 are requirements around phishing-resistant multi-factor authentication for all access to cardholder data environments. The standard now explicitly requires MFA for interactive logins — software tokens are acceptable, but hardware keys or passkeys are strongly encouraged for privileged access.",
    "For web-facing payment pages, v4.0 introduces requirements for script inventory management and integrity monitoring. Merchants hosting their own payment pages must now maintain an inventory of authorised scripts and implement change detection mechanisms — a direct response to the proliferation of web skimming attacks.",
    "If you're using Orchestrate, much of the compliance burden is handled at the platform level. Our tokenisation infrastructure means you never touch raw card data in your own systems, dramatically reducing your PCI scope. Our shared responsibility documentation outlines exactly what you remain responsible for.",
    "The deadline for full v4.0 compliance has passed, meaning auditors are now assessing against the new standard. If you haven't completed a v4.0 gap assessment, now is the time. Contact our compliance team for help understanding how Orchestrate's infrastructure maps to v4.0 requirements.",
  ],
  "reducing-chargebacks-with-dynamic-descriptors": [
    'One of the most common — and most avoidable — causes of chargebacks is simple customer confusion. A cardholder sees an unfamiliar string on their statement, doesn\'t recognise the merchant, and files a dispute. This "friendly fraud" accounts for a significant portion of chargeback volume for many businesses.',
    "Dynamic descriptors give you control over exactly what appears on a cardholder's bank statement. Rather than displaying a generic company name, you can include the specific product, location, or service that was purchased. For businesses with complex brand architectures or multiple products, this can dramatically improve statement recognition.",
    "A typical descriptor has two components: the merchant name (up to 22 characters) and a suffix (up to 13 characters). Orchestrate allows you to set both at the transaction level via the API, with sensible defaults falling back to your account-level configuration.",
    "Best practices include including the product or service name when possible, adding a customer support contact (phone number or short URL), and keeping the merchant name consistent with what the customer knows the business as — not the legal entity name.",
    "In A/B testing across our merchant base, businesses that implemented optimised dynamic descriptors saw an average 23% reduction in confusion-driven chargebacks within 90 days. The improvement is most pronounced for subscription businesses and marketplace platforms where the merchant-customer relationship is indirect.",
    "Implementing dynamic descriptors via the Orchestrate API is straightforward. Pass the statement_descriptor and statement_descriptor_suffix fields when creating a payment. Changes take effect immediately and don't require resubmission for acquirer approval.",
  ],
}

const CATEGORY_COLORS: Record<string, string> = {
  Insights: "bg-blue-light text-blue",
  Compliance: "bg-amber-light text-amber-DEFAULT",
  Product: "bg-green-light text-green-DEFAULT",
}

function BlogPostPage() {
  const params = Route.useParams()
  const post = BLOG_POSTS.find((p) => p.slug === params.slug)

  if (!post) {
    return <Navigate to="/blog" replace />
  }

  const body = POST_BODIES[post.slug] ?? []
  const relatedPosts = BLOG_POSTS.filter((p) => p.id !== post.id).slice(0, 2)

  useSEO({
    title: post.title,
    description: post.excerpt,
    keywords: `${post.category.toLowerCase()}, payment orchestration, ${post.title.toLowerCase()}`,
    canonical: `https://orchestrate.global/blog/${post.slug}`,
  })

  return (
    <>
      {/* Hero */}
      <section className="bg-surface pt-32 pb-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Back link */}
            <Link
              to="/blog"
              className="text-muted hover:text-ink group mb-8 inline-flex items-center gap-1.5 text-sm transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
              Back to Blog
            </Link>

            {/* Category + meta */}
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${CATEGORY_COLORS[post.category] ?? "bg-surface text-muted"}`}
              >
                {post.category}
              </span>
              <div className="text-muted flex items-center gap-1.5 text-xs">
                <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                {post.date}
              </div>
              <div className="text-muted flex items-center gap-1.5 text-xs">
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                {post.readTime}
              </div>
              <div className="text-muted flex items-center gap-1.5 text-xs">
                <User className="h-3.5 w-3.5" aria-hidden="true" />
                {post.author}
              </div>
            </div>

            <h1 className="font-display mb-6 text-4xl leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              {post.title}
            </h1>
            <p className="text-muted text-xl leading-relaxed">{post.excerpt}</p>
          </motion.div>
        </div>
      </section>

      {/* Article body */}
      <article className="bg-white py-12" aria-label={post.title}>
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          {/* Top divider */}
          <div className="bg-border mb-12 h-px" />

          {/* Body paragraphs */}
          <div className="prose prose-lg max-w-none space-y-6">
            {body.map((paragraph, i) => (
              <motion.p
                key={i}
                className="text-ink text-[1.0625rem] leading-[1.85]"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ once: true, amount: 0.3 }}
              >
                {paragraph}
              </motion.p>
            ))}
          </div>

          {/* Bottom divider + share */}
          <div className="border-border mt-16 flex items-center justify-between border-t pt-8">
            <div className="flex items-center gap-3">
              <div
                className="bg-blue flex h-10 w-10 items-center justify-center rounded-full text-xs font-bold text-white"
                aria-hidden="true"
              >
                {post.author.charAt(0)}
              </div>
              <div>
                <p className="text-sm font-semibold">{post.author}</p>
                <p className="text-muted text-xs">Orchestrate</p>
              </div>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/blog">
                <ArrowLeft className="h-3.5 w-3.5" /> All posts
              </Link>
            </Button>
          </div>
        </div>
      </article>

      {/* Related posts */}
      {relatedPosts.length > 0 && (
        <section className="bg-surface py-16">
          <div className="mx-auto max-w-3xl px-6 lg:px-8">
            <AnimatedSection>
              <SectionLabel label="More from the blog" className="mb-8" />
            </AnimatedSection>
            <div className="grid gap-5 sm:grid-cols-2">
              {relatedPosts.map((related, i) => (
                <AnimatedSection key={related.id} delay={i * 0.1}>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: related.slug }}
                    className="border-border hover:shadow-ink/8 group block rounded-2xl border bg-white p-5 transition-all duration-250 hover:-translate-y-1 hover:shadow-lg"
                    aria-label={`Read ${related.title}`}
                  >
                    <span
                      className={`mb-3 inline-block rounded-full px-2 py-1 text-xs font-semibold ${CATEGORY_COLORS[related.category] ?? "bg-surface text-muted"}`}
                    >
                      {related.category}
                    </span>
                    <h3 className="group-hover:text-blue mb-1 text-sm leading-snug font-semibold transition-colors">
                      {related.title}
                    </h3>
                    <p className="text-muted text-xs">{related.readTime}</p>
                    <div className="text-blue mt-3 flex items-center gap-1 text-xs font-semibold transition-all group-hover:gap-1.5">
                      Read article <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </>
  )
}
